// Marco común de las pantallas de acceso (entrar, recuperar, invitación, clave
// nueva). Es el mismo gesto del login que ya existía —mitad oscura con la marca,
// mitad clara con el formulario— sacado a un componente para que las cuatro se
// vean como UNA cosa y no como cuatro pantallas parecidas.
//
// ⚠️ Los botones y los avisos NO se redefinen aquí: `Boton` y `Aviso` ya viven
// en `packages/ui/src/piezas.tsx` y son los que usa el resto del panel. Un
// segundo botón «casi igual» es como se empieza a desalinear una interfaz.
// Lo único propio de estas pantallas son los CAMPOS de formulario, que en el
// paquete no existen.
import type { ReactNode } from "react";
import { LogoVXAI } from "@victorix/chat-ai-ui";

export function MarcoAuth(
  { titulo, bajada, children, lema }:
  { titulo: string; bajada?: string; children: ReactNode; lema?: { alto: string; bajo: string } },
) {
  const l = lema ?? {
    alto: "Todas las conversaciones\ndel negocio, en un solo lugar.",
    bajo: "El bot atiende, tú tomas el chat cuando toca, y cada cliente queda en su etapa del embudo.",
  };
  return (
    <div className="min-h-pantalla grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-[var(--color-rail)] text-white">
        <LogoVXAI className="h-9 w-auto" variant="onDark" glow />
        <div className="max-w-md">
          <h1 className="text-[34px] leading-[1.1] font-bold tracking-[-0.03em] text-white whitespace-pre-line">
            {l.alto}
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-white/60">{l.bajo}</p>
        </div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-white/35">Victorix Chat AI</p>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><LogoVXAI className="h-8 w-auto" /></div>
          <h2 className="text-[22px] font-semibold tracking-tight">{titulo}</h2>
          {bajada
            ? <p className="text-[13.5px] text-tinta-2 mt-1 mb-6 leading-relaxed">{bajada}</p>
            : <div className="mb-6" />}
          {children}
        </div>
      </div>
    </div>
  );
}

export function Campo(
  { id, etiqueta, ayuda, ...props }:
  { id: string; etiqueta: string; ayuda?: string } & React.InputHTMLAttributes<HTMLInputElement>,
) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-medium mb-1.5">{etiqueta}</label>
      <input
        id={id} {...props}
        className="w-full h-11 px-3.5 rounded-lg bg-elevado border border-borde text-[15px]
          focus:outline-none focus:border-marca/50 disabled:opacity-50"
      />
      {ayuda && <p className="mt-1.5 text-[12px] text-tinta-2 leading-relaxed">{ayuda}</p>}
    </div>
  );
}

/** Campo de contraseña con «ver/ocultar». El estado del ojo vive en React y no
 *  tocando el DOM a mano: manipular `input.type` desde el handler funciona hasta
 *  que React vuelve a pintar y lo revierte sin avisar. */
export function CampoClave(
  { id, etiqueta, valor, alCambiar, autoComplete, ayuda, ver, alVer }:
  { id: string; etiqueta: string; valor: string; alCambiar: (v: string) => void;
    autoComplete: string; ayuda?: string; ver: boolean; alVer: (v: boolean) => void },
) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-medium mb-1.5">{etiqueta}</label>
      <div className="relative">
        <input
          id={id} type={ver ? "text" : "password"} autoComplete={autoComplete} required
          value={valor} onChange={(e) => alCambiar(e.target.value)}
          className="w-full h-11 pl-3.5 pr-16 rounded-lg bg-elevado border border-borde text-[15px]
            focus:outline-none focus:border-marca/50"
        />
        <button type="button" onClick={() => alVer(!ver)}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 text-[12px] text-tinta-2
            hover:text-tinta rounded cursor-pointer">
          {ver ? "ocultar" : "ver"}
        </button>
      </div>
      {ayuda && <p className="mt-1.5 text-[12px] text-tinta-2 leading-relaxed">{ayuda}</p>}
    </div>
  );
}

/** ⚠️ La misma cuenta abre Victorix Work y Chat AI. Quien cambia su clave aquí
 *  se la cambia allá — y si no se le dice, mañana no puede entrar a Work y nadie
 *  relaciona una cosa con la otra. Este aviso NO es adorno: es la trampa nº1 del
 *  `auth.users` compartido. */
export function AvisoCuentaUnica() {
  return (
    <p className="mt-4 text-[12.5px] text-tinta-2 bg-tenue border border-borde rounded-lg
                  px-3 py-2 leading-relaxed">
      <b className="text-tinta">Tu cuenta de Victorix es una sola.</b> Esta contraseña es
      también la que usas para entrar a Victorix Work.
    </p>
  );
}

/** Pantalla de estado a pantalla completa (enlace muerto, todo listo…). */
export function Estado(
  { titulo, children, accion }:
  { titulo: string; children: ReactNode; accion?: ReactNode },
) {
  return (
    <MarcoAuth titulo={titulo}>
      <div className="text-[14px] text-tinta-2 leading-relaxed space-y-3">{children}</div>
      {accion && <div className="mt-6">{accion}</div>}
    </MarcoAuth>
  );
}
